import { useEffect, memo } from 'react';
import Lottie from 'lottie-web';
import Text from '@deprecated-ui/common/Text';
import { useTradingInfoStore } from 'global/Securities/stores/useTradingInfoStore';
import { formatNumber } from 'utils/number';
import { getDayTradeUIValue } from 'features/day-trade/utils/common';
import { formatPercentageDayTrade } from 'features/day-trade/utils/formater';
import { clevertapDayTradeDebtRatioModalViewed } from 'lib/analytics/securities';
import speedoAnimation from '../../../../../../public/animation/speedo-portfolio.json';
import {
  Modal,
  HeaderWrapper,
  ContentWrapper,
  LottieWrapper,
  InfoTextWrapper,
  DebtRatioInfoWrapper,
  RowWrapper,
  DividerWrapper,
  SymbolChip,
  RowDivider,
  DebtRatioBanner,
} from './debt-ratio.styled';

interface ModalDebtRatioProps {
  visible: boolean;
  percentage: number;
  totalDebt: number;
  marketValue: number;
  onCancel(): void;
}

function ModalDebtRatio({
  visible,
  percentage = 100,
  totalDebt,
  marketValue,
  onCancel,
}: ModalDebtRatioProps) {
  const dayTrade = useTradingInfoStore((s) => s.dayTrade);

  const { debt_ratio_rules: debtRatioRules } = dayTrade || {
    debt_ratio_rules: {
      caution: 0,
      force_sell: 0,
    },
  };

  const uiConfig = getDayTradeUIValue(
    percentage,
    debtRatioRules.force_sell,
    debtRatioRules.caution,
  );

  useEffect(() => {
    Lottie.destroy();
    Lottie.setQuality('high');

    const speedoAnim = Lottie.loadAnimation({
      container: document.getElementById('debt-speedo'),
      renderer: 'svg',
      loop: false,
      autoplay: true,
      animationData: speedoAnimation,
    });
    const speed = percentage > 50 ? 7 : 5;
    speedoAnim.setSpeed(speed);

    const maxFrame = speedoAnim.getDuration(true);
    const minPercentage = 0;
    const maxPercentage = 100;
    const percentProgress = Math.min(
      Math.max(percentage, minPercentage),
      maxPercentage,
    );

    if (percentProgress === minPercentage || !visible) {
      speedoAnim.goToAndStop(0, true);
      return;
    }

    if (percentProgress > debtRatioRules.force_sell) {
      speedoAnim.playSegments([0, maxFrame], true);
      return;
    }

    const endFrame = (percentProgress / maxPercentage) * maxFrame;
    speedoAnim.playSegments([0, endFrame], true);
  }, [visible]);

  useEffect(() => {
    if (visible) {
      clevertapDayTradeDebtRatioModalViewed();
    }
  }, [visible]);

  return (
    <Modal visible={visible} onCancel={onCancel}>
      <HeaderWrapper>
        <Text
          align="center"
          family="bold"
          size="18px"
          data-cy="debt-ratio-modal-title"
        >
          Debt Ratio
        </Text>
      </HeaderWrapper>

      <ContentWrapper>
        <LottieWrapper>
          <div id="debt-speedo" />

          <InfoTextWrapper>
            <Text size="24px" family="bold" color={uiConfig.percentColor}>
              {formatPercentageDayTrade(percentage)}
            </Text>
            <Text size="12px" color="#858585">
              Maksimal {formatPercentageDayTrade(debtRatioRules.force_sell)}
            </Text>
          </InfoTextWrapper>
        </LottieWrapper>

        <Text size="14px">
          Debt Ratio adalah perbandingan antara Day Trade yang sudah kamu pakai
          dengan market value aset Day Trade kamu saat ini.
        </Text>

        <DebtRatioInfoWrapper>
          <RowWrapper>
            <Text size="14px">Total Hutang</Text>
            <Text size="14px">{formatNumber(totalDebt)}</Text>
          </RowWrapper>

          <DividerWrapper>
            <RowDivider dashed />

            <SymbolChip>÷</SymbolChip>
          </DividerWrapper>

          <RowWrapper>
            <Text size="14px">Total Aset Day Trade (Market Value)</Text>
            <Text size="14px">{formatNumber(marketValue)}</Text>
          </RowWrapper>

          <DividerWrapper>
            <RowDivider dashed />
            <SymbolChip>=</SymbolChip>
          </DividerWrapper>

          <RowWrapper>
            <Text size="14px" family="bold">
              Debt Ratio
            </Text>
            <Text size="14px" family="bold" color={uiConfig.percentColor}>
              {formatPercentageDayTrade(percentage)}
            </Text>
          </RowWrapper>
        </DebtRatioInfoWrapper>

        <DebtRatioBanner>
          Aset Day Trade kamu akan terjual secara otomatis apabila Debt Ratio
          kamu mencapai {formatPercentageDayTrade(debtRatioRules.force_sell)}.
        </DebtRatioBanner>
      </ContentWrapper>
    </Modal>
  );
}

export default memo(ModalDebtRatio);
